import React, { useId } from 'react';
import cn from 'classnames';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';

interface SwitchProps {
  children?: React.ReactNode;
  checked: boolean;
  onChange: () => void;
}

export const Switch: React.FC<SwitchProps> = ({
  children,
  checked,
  onChange,
}) => {
  const id = useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const circleRef = React.useRef<HTMLDivElement>(null);

  /**
   * This approach is to ensure the label and icon are accessible even though
   * we're using custom styling for the checkbox.
   *
   * The handler always goes through the hidden input element to trigger the change.
   */
  const onClick = React.useCallback(() => {
    const $input = inputRef.current;

    $input?.click();
  }, []);

  return (
    <Spacing
      direction="horizontal"
      size="sm"
      className="cursor-pointer justify-between items-center"
    >
      {children && (
        <label className="font-medium cursor-pointer" htmlFor={id}>
          {children}
        </label>
      )}

      <button
        type="button"
        onClick={onClick}
        className={cn(
          'border-2 border-gray-200 bg-white rounded-xl w-11 h-6 flex items-center justify-center transition-all ease-in-out duration-300 p-[.125rem]',
        )}
      >
        <div
          className={cn('w-full h-full rounded-xl', {
            'bg-green-200': checked,
            'bg-gray-200': !checked,
          })}
        >
          <div
            ref={circleRef}
            className={cn(
              'w-4 h-full rounded-full transition-transform ease-in-out duration-300',
              {
                'translate-x-5': checked,
                'translate-x-0': !checked,
                'bg-green-700': checked,
                'bg-gray-700': !checked,
              },
            )}
          ></div>
        </div>
      </button>

      <input
        ref={inputRef}
        type="checkbox"
        id={id}
        className="form-input hidden"
        onChange={() => {
          onChange();
        }}
      />
    </Spacing>
  );
};
